'use client';

import classNames from 'classnames';
import { MouseEvent, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PageType from '@alltrails/amplitude/enums/PageType';
import logPhotoClicked from '@alltrails/amplitude/events/logPhotoClicked';
import Button from '@alltrails/denali/components/Button';
import Gallery from '@alltrails/denali/icons/Gallery';
import { PhotoHash } from '@/types/shared';
import getLocationPhotoTitle from '@/utils/getLocationPhotoTitle';
import { useLightbox } from '../Lightbox/LightboxContext';
import PhotoGridImage from './PhotoGridImage';
import styles from './PhotoHeaderGrid.module.scss';

type Props = {
  photos: PhotoHash[];
  locationDetails: any;
  pageType?: PageType;
};

const PhotoHeaderGrid = ({ photos, locationDetails, pageType }: Props) => {
  const intl = useIntl();
  const { openLightbox } = useLightbox();
  const { locationSlug, featuredLocationType } = locationDetails;

  const handleOpenLightbox = useCallback(
    (e: MouseEvent<HTMLButtonElement>, photoId: number, index: number) => {
      e.stopPropagation();
      if (pageType) {
        logPhotoClicked({
          page_type: pageType,
          photo_id: String(photoId)
        });
      }
      openLightbox(e, index);
    },
    [openLightbox, pageType]
  );

  const getTitle = useCallback(
    (photo: PhotoHash) => {
      return getLocationPhotoTitle(photo, locationDetails, intl);
    },
    [intl, locationDetails]
  );

  const buildPhotosUrl = () => {
    return `/${locationSlug}/${featuredLocationType ? `${featuredLocationType}/` : ''}photos`;
  };

  const title1 = getTitle(photos[0]);
  const title2 = getTitle(photos[1]);
  const title3 = getTitle(photos[2]);
  const hasMorePhotos = photos.length > 3;

  return (
    <div className={classNames(styles.photoHeaderGridContainer)} data-testid="photo-header-grid">
      <button
        aria-label={title1}
        className={classNames(styles.imageContainer, styles.primaryImage, hasMorePhotos && styles.gradient)}
        onClick={e => handleOpenLightbox(e, photos[0].id, 0)}
      >
        <PhotoGridImage photo={photos[0]} isPrimary />
      </button>
      {hasMorePhotos && locationDetails.type !== 'poi' && (
        <Button
          className={styles.showMore}
          size="sm"
          variant="inverse"
          testId="show-more-photos"
          text={<FormattedMessage defaultMessage="All photos" />}
          icon={{ Component: Gallery }}
          linkInfo={{ href: buildPhotosUrl() }}
        />
      )}
      <div className={classNames(styles.verticalImages)}>
        <button
          aria-label={title2}
          className={classNames(styles.imageContainer, styles.secondaryImage)}
          onClick={e => handleOpenLightbox(e, photos[1].id, 1)}
        >
          <PhotoGridImage photo={photos[1]} />
        </button>
        <button
          aria-label={title3}
          className={classNames(styles.imageContainer, styles.secondaryImage)}
          onClick={e => handleOpenLightbox(e, photos[2].id, 2)}
        >
          <PhotoGridImage photo={photos[2]} />
        </button>
      </div>
    </div>
  );
};

export default PhotoHeaderGrid;
